// Zero-state CTA routing for the Voice Ledger card: given the course data
// LessonsView already has loaded, decide the single most useful next action.
//
// Priority mirrors functions/notifications/helpers/lessonRouting.py so the
// card and the notification never point a learner in different directions:
// continue an in-progress lesson > start the next unstarted lesson >
// freestyle chat. The freestyle branch is new client behavior; LessonsView
// itself has no such fallback.
//
// "In progress" means the lesson has a dialogId and no completedAt, not
// merely a startedAt: without a dialogId there is no conversation to reopen
// (LessonsView's own click handler errors on that state), so such a lesson
// is offered as a start instead.
//
// Spec: docs/superpowers/specs/2026-07-23-voice-ledger-design.md

import { CourseStage } from "@/constants/courseStages.js";
import { isObject } from "@/scripts/rtdbLists.js";
import { findCurrentLesson, normalizePlanUnits } from "@/scripts/coursePlan.js";

const FREESTYLE = Object.freeze({
  type: "freestyle",
  lessonTitle: null,
  lesson: null,
  unitNumber: null,
  weekNumber: null,
});

/**
 * @param {unknown} courseData a single course node (courses/{uid}/{lang}/{id})
 * @param {string[]} [inflightLessonIds] coordinates ("{unit}-{week}-{lesson}")
 *   of lessons whose dialog-meta write has landed but whose lesson node the
 *   server has not stamped yet. LessonsView renders those tiles as in progress
 *   off the same list, and a card that still said "start" would contradict the
 *   tile it sits above — and invite the second tap this record exists to stop.
 * @returns {{type: "continueLesson"|"startLesson"|"freestyle",
 *   lessonTitle: string|null, lesson: object|null,
 *   unitNumber: number|null, weekNumber: number|null}}
 */
export function resolveNextAction(courseData, inflightLessonIds = []) {
  if (!isObject(courseData)) return { ...FREESTYLE };
  // Before the plan exists there is nothing to start, and after the course
  // is completed there is nothing left; both route to freestyle.
  if (courseData.metadata?.currentStage !== CourseStage.PLAN_IN_PROGRESS) {
    return { ...FREESTYLE };
  }

  // The SAME rule LessonsView locks against, from the same helper: the card's
  // CTA has to land on the lesson the view considers current, or tapping it
  // only raises the locked toast. (A deliberate departure from
  // lessonRouting.py, which prefers an in-progress lesson anywhere in the
  // plan: the notification opens its dialog server-side, with no locking.)
  const current = findCurrentLesson(normalizePlanUnits(courseData));
  if (!current) return { ...FREESTYLE };

  const { lesson } = current;
  const active = {
    lessonTitle: lesson.title ?? null,
    lesson,
    unitNumber: current.unit.number ?? null,
    weekNumber: current.week.number ?? null,
  };
  // Every lesson is done: the card's job is to get the learner speaking, and
  // reopening a completed dialog is review, not practice.
  if (lesson.completedAt) return { ...FREESTYLE };
  if (lesson.dialogId) return { ...active, type: "continueLesson" };
  // A start whose dialog-meta write has landed but whose lesson node the server
  // has not stamped yet. The tap routes into the same handleLessonClick, which
  // reopens the in-flight dialog rather than creating a second one, so
  // "continue" is what the tap actually does.
  if (
    Array.isArray(inflightLessonIds) &&
    inflightLessonIds.includes(
      `${current.unit.number}-${current.week.number}-${lesson.number}`
    )
  ) {
    return { ...active, type: "continueLesson" };
  }
  if (!lesson.startedAt) return { ...active, type: "startLesson" };
  // startedAt with no dialogId is stuck: the click handler reports
  // missingDialog rather than creating one, and everything behind this lesson
  // is locked, so no lesson CTA leads anywhere. Freestyle still works.
  return { ...FREESTYLE };
}
